import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { SEO } from "@/components/SEO";
import { api } from "@/lib/api";

const featuredPost = {
  title: "The Complete Guide to DMARC in 2026",
  excerpt:
    "Everything you need to know about implementing DMARC for your organization. From basics to advanced policies, we cover it all.",
  author: "GetSent Staff",
  authorRole: "Email Security Team",
  date: "Jan 8, 2026",
  readTime: "12 min read",
  category: "Guides",
  image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=400&fit=crop",
  slug: "complete-guide-dmarc-2026",
};

const posts = [
  {
    title: "How to Check Your DMARC Setup [Free Tool + Step-by-Step Guide]",
    excerpt:
      "Learn how to verify your DMARC configuration is working correctly. Use our free DMARC checker tool and follow this step-by-step guide.",
    author: "GetSent Staff",
    date: "Jan 15, 2026",
    readTime: "6 min read",
    category: "Guides",
    slug: "how-to-check-dmarc-setup",
  },
  {
    title: "Free SPF Checker: Validate Your SPF Records in Seconds",
    excerpt:
      "Use our free SPF checker to validate your SPF record instantly. Find DNS lookup issues, syntax errors, and missing email sources.",
    author: "GetSent Staff",
    date: "Jan 14, 2026",
    readTime: "5 min read",
    category: "Tools",
    slug: "free-spf-checker",
  },
  {
    title: "Best Free DMARC Tools for Small Business [2026 Comparison]",
    excerpt:
      "Compare the best free DMARC tools for small businesses. We review features, limitations, and help you choose the right solution.",
    author: "GetSent Staff",
    date: "Jan 12, 2026",
    readTime: "8 min read",
    category: "Comparison",
    slug: "best-free-dmarc-tools-small-business",
  },
  {
    title: "Email Authentication Checker: Is Your Domain Secure?",
    excerpt:
      "Use our free email authentication checker to verify your SPF, DKIM, and DMARC configuration. Find out if your domain is protected.",
    author: "GetSent Staff",
    date: "Jan 10, 2026",
    readTime: "7 min read",
    category: "Tools",
    slug: "email-authentication-checker",
  },
  {
    title: "Why Google and Yahoo's New Email Requirements Matter",
    excerpt:
      "Starting February 2024, bulk senders must implement DMARC. Here's what you need to know to stay compliant.",
    author: "GetSent Staff",
    date: "Jan 5, 2026",
    readTime: "5 min read",
    category: "News",
    slug: "google-yahoo-email-requirements",
  },
  {
    title: "SPF Flattening: Solving the 10 DNS Lookup Limit",
    excerpt:
      "Learn how to optimize your SPF record when you're hitting the DNS lookup limit with multiple email providers.",
    author: "GetSent Staff",
    date: "Dec 28, 2025",
    readTime: "8 min read",
    category: "Technical",
    slug: "spf-flattening-dns-lookup-limit",
  },
  {
    title: "Understanding DKIM Key Rotation Best Practices",
    excerpt:
      "Regular key rotation is essential for security. We explain when and how to rotate your DKIM keys safely.",
    author: "GetSent Staff",
    date: "Dec 20, 2025",
    readTime: "6 min read",
    category: "Security",
    slug: "dkim-key-rotation-best-practices",
  },
  {
    title: "Case Study: How Acme Corp Achieved p=reject in 30 Days",
    excerpt:
      "Follow along as we walk through a real-world DMARC implementation from monitoring to full enforcement.",
    author: "GetSent Staff",
    date: "Dec 15, 2025",
    readTime: "10 min read",
    category: "Case Study",
    slug: "case-study-acme-corp-dmarc",
  },
  {
    title: "Email Authentication for SaaS: A Practical Guide",
    excerpt:
      "Running a SaaS product that sends transactional emails? Here's how to set up authentication properly.",
    author: "GetSent Staff",
    date: "Dec 10, 2025",
    readTime: "7 min read",
    category: "Guides",
    slug: "email-authentication-saas-guide",
  },
  {
    title: "Analyzing DMARC Reports: What the Data Tells You",
    excerpt:
      "DMARC reports contain a wealth of information. Learn how to interpret the data and take action.",
    author: "GetSent Staff",
    date: "Dec 5, 2025",
    readTime: "9 min read",
    category: "Technical",
    slug: "analyzing-dmarc-reports",
  },
];

const categories = ["All", "Guides", "Tools", "Technical", "News", "Security", "Case Study", "Comparison"];

export function BlogPage() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus("loading");
    try {
      const result = await api.newsletter.subscribe(email, "blog");
      setStatus("success");
      setMessage(result.message);
      setEmail("");
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again.");
    }
  };

  return (
    <div>
      <SEO
        title="Blog"
        description="Email security insights, DMARC guides, and best practices for protecting your domain. Stay updated with the latest in email authentication."
        canonical="/blog"
      />
      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-indigo-50/50 to-transparent py-16 dark:from-indigo-950/20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              The GetSent{" "}
              <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
                Blog
              </span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Expert insights on email authentication, security best practices, and industry news.
            </p>
          </div>
        </div>
      </section>

      {/* Featured post */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Link
            to="/blog/$slug"
            params={{ slug: featuredPost.slug }}
            className="group grid gap-8 rounded-3xl border bg-card p-6 transition-all hover:border-indigo-500/50 hover:shadow-xl lg:grid-cols-2 lg:p-0"
          >
            <div className="overflow-hidden rounded-2xl lg:rounded-l-3xl lg:rounded-r-none">
              <div
                className="aspect-[16/9] h-full w-full bg-cover bg-center transition-transform duration-300 group-hover:scale-105 lg:aspect-auto"
                style={{ backgroundImage: `url(${featuredPost.image})` }}
              />
            </div>
            <div className="flex flex-col justify-center lg:p-8 lg:pr-12">
              <div className="flex items-center gap-3">
                <span className="rounded-full bg-indigo-500/10 px-3 py-1 text-xs font-semibold text-indigo-600">
                  {featuredPost.category}
                </span>
                <span className="text-sm text-muted-foreground">Featured</span>
              </div>
              <h2 className="mt-4 text-2xl font-bold lg:text-3xl">
                {featuredPost.title}
              </h2>
              <p className="mt-3 text-muted-foreground">{featuredPost.excerpt}</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600" />
                <div>
                  <div className="text-sm font-medium">{featuredPost.author}</div>
                  <div className="text-xs text-muted-foreground">
                    {featuredPost.date} · {featuredPost.readTime}
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Category filter */}
      <section className="border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto pb-4">
            {categories.map((category) => (
              <button
                key={category}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  category === "All"
                    ? "bg-foreground text-background"
                    : "bg-muted hover:bg-muted/80"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <Link
                key={post.title}
                to="/blog/$slug"
                params={{ slug: post.slug }}
                className="group flex flex-col rounded-2xl border bg-card transition-all hover:border-indigo-500/50 hover:shadow-lg"
              >
                <div className="p-6">
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium">
                      {post.category}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {post.readTime}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold group-hover:text-indigo-600">
                    {post.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                    {post.excerpt}
                  </p>
                </div>
                <div className="mt-auto border-t p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600" />
                      <span className="text-sm font-medium">{post.author}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{post.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Load more */}
          <div className="mt-12 text-center">
            <button className="rounded-full border bg-background px-8 py-3 font-medium transition-colors hover:bg-muted">
              Load more posts
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="border-t bg-muted/30 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-xl text-center">
            <h2 className="text-2xl font-bold">Subscribe to our newsletter</h2>
            <p className="mt-2 text-muted-foreground">
              Get the latest email security insights delivered to your inbox.
            </p>
            {status === "success" ? (
              <div className="mt-6 rounded-lg bg-green-50 p-4 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                {message}
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="mt-6 flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="flex-1 rounded-full border bg-background px-5 py-3 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-3 font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-50"
                >
                  {status === "loading" ? "..." : "Subscribe"}
                </button>
              </form>
            )}
            {status === "error" && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">{message}</p>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
